import { Redirect, useParams } from 'react-router';
import { Stack } from '@chakra-ui/react';

import { AccountingExportOption } from '__generated__/GQL';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { useModal } from 'components/ModalProvider/hooks';
import * as Layout from 'layouts/ContentLayout';

import ExportOptions from '../../components/ExportOptions';
import * as routes from '../../routes';
import { formatExportNextDate } from '../../utils';
import CancelExportModal from './components/CancelExportModal';
import useGetRecurringExport from './hooks/useGetRecurringExport';
import locales from './locales';

const MODAL_TYPE = 'cancel-recurring-export';

export interface RouteState {
  exportId: string;
}

const RecurringExportDetails = () => {
  const { exportId } = useParams<RouteState>();
  const { error, loading, recurringExport } = useGetRecurringExport(exportId);
  const { isModalOpen, showModal } = useModal();

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorView />;
  }

  if (!recurringExport) {
    return <Redirect to={routes.exportDetails} />;
  }

  const recurringExportOptions =
    recurringExport.options as AccountingExportOption[];
  const disabledExportOptions = [
    AccountingExportOption.BankStatements,
    AccountingExportOption.Invoices,
    AccountingExportOption.Receipts,
    AccountingExportOption.Transactions,
  ];

  return (
    <Layout.Root>
      <Layout.Body>
        <Stack spacing="space-32">
          <BackButton to={routes.exports} />

          <Heading marginBottom="space-16" size="lg">
            {locales.title}
          </Heading>

          <Text size="lg">
            {locales.formatString(locales.nextReportDate, {
              date: formatExportNextDate(),
            })}
          </Text>

          {recurringExport.recipient ? (
            <Stack spacing="space-16">
              <Form.Field label={locales.inputNameLabel}>
                <Form.Input isDisabled value={recurringExport.recipient.name} />
              </Form.Field>

              <Form.Field label={locales.inputEmailLabel}>
                <Form.Input
                  isDisabled
                  value={recurringExport.recipient.email}
                />
              </Form.Field>
            </Stack>
          ) : null}

          <ExportOptions
            disabledIds={disabledExportOptions}
            onChange={() => null}
            selectedIds={recurringExportOptions}
          />

          <Button
            alignSelf="flex-start"
            icon="trash"
            onClick={() => showModal(MODAL_TYPE)}
            variant="danger"
          >
            {locales.cancelExport}
          </Button>

          {isModalOpen(MODAL_TYPE) ? <CancelExportModal /> : null}
        </Stack>
      </Layout.Body>
    </Layout.Root>
  );
};

export default RecurringExportDetails;
